import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { Martini, ArrowRight } from "lucide-react";
import { useReveal } from "../hooks/useReveal";
import { IMAGES } from "../mock";

// Generate randomized droplet params for the splatter burst
function makeDroplets(count) {
  const arr = [];
  for (let i = 0; i < count; i++) {
    const angle = (-90 + (Math.random() * 150 - 75)) * (Math.PI / 180); // fan upward
    const dist = 70 + Math.random() * 230;
    const dx = Math.cos(angle) * dist;
    const dy = Math.sin(angle) * dist - Math.random() * 60; // bias upward
    arr.push({
      id: i,
      size: 4 + Math.random() * 11,
      dx: `${dx.toFixed(0)}px`,
      dy: `${dy.toFixed(0)}px`,
      ds: (0.6 + Math.random() * 0.9).toFixed(2),
      delay: (Math.random() * 2.6).toFixed(2),
      duration: (1.7 + Math.random() * 1.8).toFixed(2),
      left: 40 + (Math.random() * 16 - 8), // origin near glass rim (%)
      top: 40 + (Math.random() * 10 - 5),
    });
  }
  return arr;
}

export default function SplashFeature() {
  const { ref, inView } = useReveal({ threshold: 0.35 });
  const droplets = useMemo(() => makeDroplets(26), []);

  return (
    <section ref={ref} className="relative bg-nala-black overflow-hidden">
      <div className="grid lg:grid-cols-2 items-stretch">
        {/* Text side */}
        <div className="relative flex items-center order-2 lg:order-1 px-6 md:px-12 lg:px-16 py-16 lg:py-0">
          <div className={`max-w-lg reveal ${inView ? "in-view" : ""}`}>
            <p className="text-nala-gold tracking-luxe uppercase text-sm mb-5 flex items-center gap-3">
              <Martini size={16} /> The Bar in Motion
            </p>
            <h2 className="font-display text-4xl md:text-5xl leading-tight text-nala-cream mb-6">
              Island <span className="text-gold-gradient">Ember</span>
            </h2>
            <p className="font-serif-lux text-xl text-nala-cream-muted leading-relaxed mb-4">
              Every pour is a performance. Aged rum, passion fruit, and a whisper of smoke —
              shaken tableside and served with theatrical flair.
            </p>
            <p className="font-serif-lux text-lg text-nala-cream-muted/80 leading-relaxed mb-9">
              Watch the splash. Feel the moment. This is Nala's craft, uncorked.
            </p>
            <Link to="/menu" className="btn-gold">
              <span>Explore Cocktails</span>
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>

        {/* Splash image side */}
        <div className="relative order-1 lg:order-2 h-[70vh] lg:h-auto min-h-[520px] overflow-hidden">
          <div className={`relative w-full h-full splash-glass ${inView ? "splash-active" : ""}`}>
            <img
              src={IMAGES.ckSplash}
              alt="Island Ember cocktail splashing"
              className="absolute inset-0 w-full h-full object-cover object-[50%_28%]"
            />
            {/* left fade so it blends into text side on desktop */}
            <div className="absolute inset-0 bg-gradient-to-r from-nala-black via-transparent to-transparent lg:from-nala-black/90 lg:via-nala-black/0" />
            <div className="absolute inset-0 bg-gradient-to-t from-nala-black/40 via-transparent to-transparent" />

            {/* Golden mist glow near splash */}
            <div
              className="pointer-events-none absolute rounded-full bg-nala-gold/20 blur-3xl"
              style={{ width: 220, height: 220, left: "38%", top: "30%", animation: inView ? "mistFloat 5s ease-in-out infinite" : "none" }}
            />

            {/* Animated droplets */}
            <div className={`absolute inset-0 pointer-events-none ${inView ? "splash-active" : ""}`}>
              {droplets.map((d) => (
                <span
                  key={d.id}
                  className="splash-droplet"
                  style={{
                    width: d.size,
                    height: d.size,
                    left: `${d.left}%`,
                    top: `${d.top}%`,
                    // custom props consumed by keyframes
                    "--dx": d.dx,
                    "--dy": d.dy,
                    "--ds": d.ds,
                    animationDelay: `${d.delay}s`,
                    animationDuration: `${d.duration}s`,
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
